import { z } from "zod"
import { DateOnly } from "@/lib/date-only"
import { debouncedValidatePhoneNumber } from "@/lib/validate-phone-number"
import { stringRequired } from "@/models/common"
import { isMedgateConnect } from "@/modules/patients/agenda/teleconsultation/booking/constants/medgate-connect"
import { chPartnerUsersQuery$data } from "@/modules/patients/agenda/teleconsultation/booking/services/__generated__/chPartnerUsersQuery.graphql"
import { phoneNumberValidation } from "@/modules/patients/components/phoneNumber/PhoneNumberInput"
import { PHONE_COUNTRY_CODE_PREFIX_MIN_LENGTH } from "@/modules/patients/constants/Patient"
import {
  AppointmentType,
  CommunicationChannel,
  ContactChannel,
  Flexibility,
  Gender,
  Language,
  PartnerType,
  RequestType,
  Service,
  ServiceSpecialty,
  Urgency,
} from "@/types/__generated__/union-types"

export type PartnerUser = NonNullable<NonNullable<chPartnerUsersQuery$data["ch_partnerUsers"]>["nodes"]>[number]

export const ADDRESS_SOURCES = {
  HOME: "HOME",
  PARTNER: "PARTNER",
  PARTNER_LOCATION: "PARTNER_LOCATION",
  MANUAL: "MANUAL",
} as const
export type AddressSource = (typeof ADDRESS_SOURCES)[keyof typeof ADDRESS_SOURCES]

export const isPartnerAddressSource = (source?: AddressSource) =>
  source === ADDRESS_SOURCES.PARTNER || source === ADDRESS_SOURCES.PARTNER_LOCATION

const phoneNumberSchema = phoneNumberValidation(debouncedValidatePhoneNumber())

export const teleconsultationSchema = (contactChannel: ContactChannel) =>
  z
    .object({
      // Address
      addressSource: z.custom<AddressSource>().optional(),
      postalCode: z.string().nullable(),
      city: z.string().nullable(),
      country: z.string().nullable(),
      houseNumberStreet: z.string().nullable(),
      // Appointment
      id: z.string().nullable(),
      date: z.custom<DateOnly>(),
      slotId: z.number().min(0).max(47).nullable(),
      urgency: z.custom<Urgency>(),
      flexibility: z.custom<Flexibility>(),
      appointmentType: z.custom<AppointmentType>(),
      followUpForProgressNoteId: z.string().optional(),
      channel: z.custom<CommunicationChannel>(),
      patientPhoneNumber: z.string().max(20).nullable(),
      service: z.custom<Service>(),
      specialty: z.custom<ServiceSpecialty>(),
      requestType: z.custom<RequestType>(),
      gender: z.custom<Gender>().nullable(),
      agentUserName: z.string().nullable(),
      language: z.custom<Language>(),
      reason: stringRequired,
      preventionProgramKeys: z.array(z.string()).optional().default([]),
      assignedPartner: z.string().nullable(),
      partnerType: z.custom<PartnerType>().nullable(),
      isPartnerDoc2U: z.boolean().nullable(),
    })
    .superRefine(async (data, ctx) => {
      // Slot
      if (data.urgency === "NORMAL" && data.slotId === null) {
        ctx.addIssue({
          code: "custom",
          path: ["slotId"],
          message: "patient.agenda.teleconsultationForm.slotRequired",
        })
      }

      // Address source required
      if (!data.addressSource || (isPartnerAddressSource(data.addressSource) && !isMedgateConnect(data.service))) {
        ctx.addIssue({
          code: "custom",
          path: ["addressSource"],
          message: "common.fieldErrorList.requiredField",
        })
      }

      // Manual address fields required
      if (data.addressSource === "MANUAL") {
        if (!data.country || data.country.length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["country"],
            message: "common.fieldErrorList.requiredField",
          })
        }
        if (!data.postalCode || data.postalCode.length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["postalCode"],
            message: "common.fieldErrorList.requiredField",
          })
        }
        if (!data.city || data.city.length === 0) {
          ctx.addIssue({ code: "custom", path: ["city"], message: "common.fieldErrorList.requiredField" })
        }
        if (!data.houseNumberStreet || data.houseNumberStreet.length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["houseNumberStreet"],
            message: "common.fieldErrorList.requiredField",
          })
        }
      }

      // Follow-up
      if (data.appointmentType === "FOLLOW_UP" && !data.followUpForProgressNoteId) {
        ctx.addIssue({
          code: "custom",
          path: ["followUpForProgressNoteId"],
          message: "common.fieldErrorList.requiredField",
        })
      }

      // Medgate Connect
      if (isMedgateConnect(data.service)) {
        if (!data.assignedPartner) {
          ctx.addIssue({
            code: "custom",
            path: ["assignedPartner"],
            message: "common.fieldErrorList.requiredField",
          })
        }
      }

      // Phone number
      const areVideoAndNoContactChannel =
        (contactChannel === "NO_CHANNEL" || contactChannel === "EMAIL_AND_SMS") && data.channel === "VIDEO"

      if (data.addressSource !== "PARTNER" && (data.channel === "PHONE" || areVideoAndNoContactChannel)) {
        if (!data.patientPhoneNumber || data.patientPhoneNumber.length <= PHONE_COUNTRY_CODE_PREFIX_MIN_LENGTH) {
          ctx.addIssue({
            code: "custom",
            path: ["patientPhoneNumber"],
            message: "common.fieldErrorList.requiredField",
          })
          return z.NEVER
        }

        const phoneResult = await phoneNumberSchema.safeParseAsync(data.patientPhoneNumber)
        if (!phoneResult.success) {
          ctx.addIssue({
            code: "custom",
            path: ["patientPhoneNumber"],
            message: phoneResult.error.issues[0]?.message || "common.fieldErrorList.invalidFormat",
          })
        }
      }
    })

export type TeleconsultationForm = z.infer<ReturnType<typeof teleconsultationSchema>>
export type TeleconsultationFormInput = z.input<ReturnType<typeof teleconsultationSchema>>
